// src/hooks/useContactNames.js
// Fax Contacts names for hovering a number in Outbox, Inbox and Trash
// (3.25.0): the client's own name for a number, else its phone listing,
// else "No name" (3.30.7: only the name, nothing more).
// useContactNames(rows, fields) asks only for the numbers on the page
// (rows' accountId + the given number fields) and remembers the answers
// until the client is switched. → hoverFor(accountId, number) → text or ''
// ('' only while the answer is still on its way).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { API_BASE } from '../firebase/config';

const last10 = n => String(n || '').replace(/\D/g, '').slice(-10);
const known = new Map();   // "<accountId>|<10 digits>" → { name, listing, checked }; per browser tab
const asking = new Set();  // pairs being fetched right now
let knownFor = null;
const listeners = new Set();   // mounted hooks, told when a name is saved (3.68.7)

// 3.68.7: after a name is saved (fax panel), ask again for that number on
// every page that shows it
export function forgetContactName(accountId, number) {
  known.delete(`${accountId}|${last10(number)}`);
  listeners.forEach(fn => fn());
}
// The raw answer for one number: { name, listing, checked } or null
export function knownContact(accountId, number) {
  return (accountId && known.get(`${accountId}|${last10(number)}`)) || null;
}

export function useContactNames(rows, fields) {
  const { getToken, accountVersion } = useAuth();
  const [tick, setTick] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const bump = () => setTick(t => t + 1);
    listeners.add(bump);
    return () => { mounted.current = false; listeners.delete(bump); };
  }, []);
  if (knownFor !== accountVersion) { known.clear(); asking.clear(); knownFor = accountVersion; }
  // This page's numbers, as one string so a new array with the same rows
  // doesn't ask again
  const out = new Set();
  for (const r of rows || []) {
    if (!r?.accountId) continue;
    for (const f of fields) { const d = last10(r[f]); if (d.length === 10) out.add(`${r.accountId}|${d}`); }
  }
  const key = [...out].sort().join(',');
  useEffect(() => {
    const missing = (key ? key.split(',') : []).filter(p => !known.has(p) && !asking.has(p));
    if (!missing.length) return;
    missing.forEach(p => asking.add(p));
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/contacts/names`, {
          method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ pairs: missing }),
        });
        const j = await r.json();
        missing.forEach(p => known.set(p, (j.names || {})[p] || { name: '', listing: '', checked: false }));
        if (mounted.current) setTick(t => t + 1);
      } catch { /* hover names are optional */ }
      missing.forEach(p => asking.delete(p));
    })();
  }, [key, getToken, tick]);
  return useCallback((accountId, number) => {
    const x = accountId && known.get(`${accountId}|${last10(number)}`);
    if (!x) return '';
    // 3.30.7: just the name — the team's own name in Contacts, else the
    // phone listing; no explanations
    return x.name || x.listing || 'No name';
  }, []);
}
